import { Pipe, PipeTransform, Type, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ENVIRONMENT, PIMSEnvironment } from 'environments';
import { replaceWidgetsInContent, WidgetConfigMap, WidgetType } from '@shared-utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { WidgetRefillResponseComponent } from '@widgets';
import { Message, MessageLink } from '../../../../util/src/index';

const WIDGETS_CONFIG: Partial<WidgetConfigMap> = {
  [WidgetType.REFILL]: { permissions: ['clinician'], enabled: true, previewToken: true },
  [WidgetType.REFILL_RESPONSE]: { permissions: ['patient'], enabled: true },
  [WidgetType.WL_FILE_UPLOAD]: { permissions: ['clinician'], enabled: true, previewToken: true },
};

const WIDGET_TAG_RE = /<lib-widget-([\w-]+)\s+\[data\]='([^']*)'><\/lib-widget-\1>/gi;

const WIDGET_REGISTRY: Record<string, Type<unknown>> = {
  'refill-response': WidgetRefillResponseComponent,
};

export interface WidgetInstance {
  component: Type<unknown>;
  inputs: { data: Record<string, unknown> };
}

export interface MessageContentResult {
  html: SafeHtml;
  widgets: WidgetInstance[];
}

@Pipe({ name: 'messageContent', standalone: true })
export class MessageContentPipe implements PipeTransform {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly app = inject<PIMSEnvironment>(ENVIRONMENT).constants.app_name;
  private readonly cache = new Map<number, MessageContentResult>();

  transform(message: Message): MessageContentResult {
    if (!message?.content) return { html: '', widgets: [] };
    if (this.cache.has(message.id)) return this.cache.get(message.id)!;

    let content = message.content;

    // 0. Sanitise user text before any markup generation (behind feature flag).
    if (FEATURE_BODACDC_2674) {
      // Escape HTML so nothing the user typed can become a live element.
      content = escapeHtml(content);
      // Break Angular interpolation delimiters to prevent expression injection.
      content = neutralizeInterpolation(content);
    }

    // 1. Widget filter — replace [[widget:...]] tokens with HTML
    content = replaceWidgetsInContent(content, message.to.id, WIDGETS_CONFIG, message.id, this.app);

    // 2. Link parser — visit refs + message.links → <a> tags
    content = parseLinks(content, message.links);

    // 3. Decode HTML entities
    content = decodeHtmlEntities(content);

    // 4. Extract widget tags — they can't render via innerHTML
    const widgets: WidgetInstance[] = [];
    content = content.replace(WIDGET_TAG_RE, (_match, name, json) => {
      const component = WIDGET_REGISTRY[name];
      if (!component) return _match;
      try {
        widgets.push({ component, inputs: { data: JSON.parse(json) } });
      } catch {
        /* ignore malformed */
      }
      return '';
    });

    const result: MessageContentResult = {
      html: this.sanitizer.bypassSecurityTrustHtml(content),
      widgets,
    };
    this.cache.set(message.id, result);
    return result;
  }
}

function parseLinks(content: string, links?: MessageLink[]): string {
  content = content.replace(/visit:\s(\d+)/gim, (_match, id) => `<a href="/visits/${id}">Visit: ${id}</a>`);

  if (!links?.length) return content;

  for (const link of links) {
    if (link.type === 'couponlink') {
      const tag = ':{PRICING_LINK}';
      const start = content.indexOf(tag);
      if (start === -1) continue;

      let literalLink: string;
      if (link.details.charAt(0) === '[') {
        const details = JSON.parse(link.details) as string[];
        literalLink = details.map(d => `<a target="_blank" href="${d}"> ${d}</a>`).join('');
      } else {
        literalLink = `<a target="_blank" href="${link.details}"> ${link.details}</a>`;
      }
      content = spliceString(content, start, tag.length, literalLink);
    } else {
      const label = content.substring(link.start, link.start + link.length);
      let href = link.details.replace('com.polkadoc.ocp://', '').replace('pages', 'pages').replace(/\s/g, '_');

      if (href.lastIndexOf('/') === href.length - 1) {
        href = href.substring(0, href.lastIndexOf('/'));
      }
      if (href.indexOf('http') === -1) {
        href = `/${href}`;
      }

      const literalLink = `<a target="_blank" href="${href}"> ${label}</a>`;
      content = spliceString(content, link.start, link.length, literalLink);
    }
  }

  return content;
}

/** Escape user text so it cannot be interpreted as HTML. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Break interpolation delimiters with a zero-width space to prevent expression injection. */
const ZWSP = '\u200B';
function neutralizeInterpolation(text: string): string {
  return text.replace(/{{/g, `{${ZWSP}{`).replace(/}}/g, `}${ZWSP}}`);
}

function decodeHtmlEntities(content: string): string {
  if (!content) return content;
  const txt = document.createElement('textarea');
  txt.innerHTML = content;
  let result = txt.value;
  // Second pass for double-encoded entities (e.g. &amp;quot; → &quot; → ")
  if (result !== content) {
    txt.innerHTML = result;
    result = txt.value;
  }
  return result;
}

function spliceString(str: string, index: number, length: number, replacement: string): string {
  return str.slice(0, index) + replacement + str.slice(index + Math.abs(length));
}
